type GetId<T> = (node: T) => string;

export class TreeSelectionManager<T extends { children?: T[] }> {
  private readonly nodeMap: Map<string, T>;
  private readonly getId: GetId<T>;

  private constructor(nodes: T | T[], getId: GetId<T>) {
    this.getId = getId;
    this.nodeMap = new Map();
    const nodeArray = Array.isArray(nodes) ? nodes : [nodes];
    this.buildNodeMap(nodeArray);
  }

  static for<T extends { children?: T[] }>(nodes: T | T[], getId: GetId<T>): TreeSelectionManager<T> {
    return new TreeSelectionManager(nodes, getId);
  }

  private buildNodeMap(nodes: T[]): void {
    const addToMap = (node: T) => {
      this.nodeMap.set(this.getId(node), node);
      node.children?.forEach(addToMap);
    };

    nodes.forEach(addToMap);
  }

  private getParentId(nodeId: string): string {
    return nodeId.split("/").slice(0, -1).join("/");
  }

  private getNodeDepth(nodeId: string): number {
    return nodeId.split("/").length - 1;
  }

  private getAllChildren(node: T): string[] {
    const children: string[] = [];
    const traverse = (n: T) => {
      children.push(this.getId(n));
      n.children?.forEach(traverse);
    };
    node.children?.forEach(traverse);
    return children;
  }

  private areAllChildrenSelected(node: T, selected: Set<string>): boolean {
    return node.children?.every((child: T) => selected.has(this.getId(child))) ?? false;
  }

  decompress(selectedIds: string[]): string[] {
    const expanded = new Set<string>();

    selectedIds.forEach((id) => {
      const node = this.nodeMap.get(id);
      if (node) {
        expanded.add(id);
        this.getAllChildren(node).forEach((childId) => expanded.add(childId));
      }
    });

    return Array.from(expanded);
  }

  compress(selectedIds: string[]): string[] {
    const consolidated = new Set(selectedIds);

    // Get all selected nodes and sort by depth (descending - deepest first)
    const selectedNodes = selectedIds
      .map((id) => this.nodeMap.get(id))
      .filter((node): node is T => !!node)
      .sort((a, b) => this.getNodeDepth(this.getId(b)) - this.getNodeDepth(this.getId(a)));

    // Process each selected node
    selectedNodes.forEach((node) => {
      const nodeId = this.getId(node);
      // Skip if this node was already processed
      if (!consolidated.has(nodeId)) {
        return;
      }

      // If node has children, remove their selections
      if (node.children?.length) {
        const childIds = this.getAllChildren(node);
        childIds.forEach((id) => consolidated.delete(id));
      }

      // Check if we should consolidate to parent
      const parentId = this.getParentId(nodeId);
      const parent = this.nodeMap.get(parentId);

      if (parent && this.areAllChildrenSelected(parent, consolidated)) {
        // Remove all child selections
        parent.children?.forEach((child: T) => consolidated.delete(this.getId(child)));
        // Add parent selection
        consolidated.add(this.getId(parent));
      }
    });

    let returnValue = Array.from(consolidated);

    if (selectedIds.length !== returnValue.length) {
      returnValue = this.compress(returnValue);
    }

    return returnValue;
  }
}
